import React, { useEffect, useRef, useState } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { listCollectionPointsForPartner } from '../modules/collection-points/api';
import { listSitesForPartner } from '../modules/sites/api';
import { listSchedulesForPartner } from '../modules/order-schedules/api';

const WEEKDAY_LETTERS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

export const boxLabel = (p) => {
  if (!p) return '';
  const ref = p.externalRef && !String(p.name || '').includes(p.externalRef) ? ` (${p.externalRef})` : '';
  return `${p.name}${ref}`;
};

export const scheduleLabel = (s) => {
  if (!s) return '';
  const where = s.pickupSite?.name || s.deliverySite?.name || '';
  const days = Array.isArray(s.weekdays) ? s.weekdays.map((on, i) => (on ? WEEKDAY_LETTERS[i] : '·')).join(' ') : '';
  return [`#${s.id}`, where, days].filter(Boolean).join(' — ');
};

/**
 * #132 — pick the box / site / recurring schedule a ticket is about, inside an
 * antd Form (fields `collectionPointId`, `siteId`, `orderScheduleId`). Options
 * are the given partner's. A box already implies its site (the backend derives
 * it), so choosing one clears the other. Sites are searched server-side (some
 * partners have thousands); boxes and schedules are listed whole.
 * `current` = the ticket's linked entities, so a saved link shows its name
 * before any list has loaded.
 */
export default function TicketLinkFields({ form, partnerId, current = {} }) {
  const { t } = useTranslation(['routes', 'common']);
  const [boxes, setBoxes] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const searchTimer = useRef(null);

  // A partner change makes the picked links meaningless (another partner's
  // boxes) — clear them. Not on mount: an existing ticket keeps its links.
  const prevPartner = useRef(partnerId);
  useEffect(() => {
    // (useWatch starts undefined, then reports the initial value — not a change.)
    if (prevPartner.current !== undefined && prevPartner.current !== partnerId) {
      form.setFieldsValue({ collectionPointId: undefined, siteId: undefined, orderScheduleId: undefined });
    }
    prevPartner.current = partnerId;
  }, [partnerId, form]);

  useEffect(() => {
    setBoxes([]);
    setSchedules([]);
    setSites([]);
    if (!partnerId) return;
    listCollectionPointsForPartner(partnerId)
      .then(({ response }) => setBoxes(Array.isArray(response) ? response : []))
      .catch(() => {});
    listSchedulesForPartner(partnerId)
      .then(({ response }) => setSchedules(Array.isArray(response) ? response : []))
      .catch(() => {});
  }, [partnerId]);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const searchSites = (q) => {
    clearTimeout(searchTimer.current);
    if (!partnerId || !q || q.trim().length < 2) return;
    searchTimer.current = setTimeout(() => {
      setLoadingSites(true);
      listSitesForPartner(partnerId, q.trim())
        .then(({ response }) => setSites(Array.isArray(response) ? response.slice(0, 50) : []))
        .catch(() => {})
        .finally(() => setLoadingSites(false));
    }, 300);
  };

  const withCurrent = (options, entity, label) =>
    entity && !options.some((o) => o.value === entity.id) ? [{ value: entity.id, label }, ...options] : options;

  const boxOptions = withCurrent(
    boxes.map((p) => ({
      value: p.id,
      label: [boxLabel(p), p.site?.name].filter(Boolean).join(' — '),
    })),
    current.collectionPoint,
    boxLabel(current.collectionPoint),
  );
  const siteOptions = withCurrent(
    sites.map((s) => ({ value: s.id, label: [s.name, s.address].filter(Boolean).join(' — ') })),
    // A site derived from the box isn't a link of its own — don't offer it.
    current.collectionPointId ? null : current.site,
    current.site?.name,
  );
  const scheduleOptions = withCurrent(
    schedules.map((s) => ({ value: s.id, label: scheduleLabel(s) })),
    current.orderSchedule,
    `#${current.orderSchedule?.id}`,
  );

  const disabled = !partnerId;
  const placeholder = (key) => (disabled ? t('tickets.pickPartnerFirst') : t(key));

  return (
    <>
      <Form.Item name="collectionPointId" label={t('tickets.linkBox')} tooltip={t('tickets.linkBoxHint')}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          disabled={disabled}
          placeholder={placeholder('tickets.linkOptional')}
          options={boxOptions}
          onChange={(v) => v && form.setFieldValue('siteId', undefined)}
          data-testid="ticket-link-box"
        />
      </Form.Item>
      <Form.Item name="siteId" label={t('tickets.linkSite')}>
        <Select
          allowClear
          showSearch
          filterOption={false}
          onSearch={searchSites}
          loading={loadingSites}
          disabled={disabled}
          placeholder={placeholder('tickets.linkSiteSearch')}
          notFoundContent={null}
          options={siteOptions}
          onChange={(v) => v && form.setFieldValue('collectionPointId', undefined)}
          data-testid="ticket-link-site"
        />
      </Form.Item>
      <Form.Item name="orderScheduleId" label={t('tickets.linkSchedule')}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          disabled={disabled}
          placeholder={placeholder('tickets.linkOptional')}
          options={scheduleOptions}
          data-testid="ticket-link-schedule"
        />
      </Form.Item>
    </>
  );
}
